import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LoaderCircle, ArrowLeft, Shield } from "lucide-react";
import { Logo } from "../components/landing/Logo";
import { formatApiError } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/admin/enterprise/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Login failed");
      localStorage.setItem("funz_admin_token", data.token);
      localStorage.setItem("funz_admin", JSON.stringify(data.admin));
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatApiError(err.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-login" className="grid-bg relative flex min-h-screen items-center justify-center overflow-hidden bg-[var(--bg-base)] px-4 text-[var(--text-primary)]">
      <div className="noise-overlay" aria-hidden="true" />
      <div aria-hidden="true" className="absolute -left-40 top-20 h-[420px] w-[420px] rounded-full bg-[#7B61FF]/20 blur-[140px]" />
      <div aria-hidden="true" className="absolute -right-32 bottom-10 h-[380px] w-[380px] rounded-full bg-[#00D4FF]/15 blur-[140px]" />
      <div className="relative z-10 w-full max-w-md">
        <Link to="/" data-testid="admin-back" className="mb-6 inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Back to FUNZ
        </Link>
        <div className="glass rounded-[2rem] p-8 sm:p-10">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#00D4FF] to-[#7B61FF]">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="font-display text-xl font-extrabold">Enterprise Admin</p>
              <p className="font-jbmono text-[10px] uppercase tracking-[0.25em] text-[#00D4FF]">Secure access</p>
            </div>
          </div>
          <form onSubmit={submit} className="mt-7 space-y-4" data-testid="admin-form">
            <input data-testid="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@funz.store"
              className="w-full rounded-2xl border border-white/10 bg-[#09090B]/80 px-5 py-4 text-sm text-white placeholder:text-slate-500 outline-none focus:border-[#00D4FF]/60" />
            <input data-testid="admin-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
              className="w-full rounded-2xl border border-white/10 bg-[#09090B]/80 px-5 py-4 text-sm text-white placeholder:text-slate-500 outline-none focus:border-[#00D4FF]/60" />
            {error && <p data-testid="admin-error" className="rounded-xl border border-[#EF4444]/30 bg-[#EF4444]/10 px-4 py-3 text-sm text-[#EF4444]">{error}</p>}
            <button data-testid="admin-submit" type="submit" disabled={busy}
              className="btn-primary flex w-full items-center justify-center gap-2 rounded-full py-4 font-display text-sm font-bold uppercase tracking-wider text-white disabled:opacity-60">
              {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : "Sign in to admin"}
            </button>
          </form>
          <p className="mt-6 text-center text-xs text-slate-500">Enterprise admin accounts only. 5 failed attempts locks the account.</p>
        </div>
      </div>
    </div>
  );
}
